import { useTranslation } from "react-i18next";

import type { IntegrationModuleChip } from "../types";
import { orderAndSplitChips } from "./IntegrationModuleChips.utils";

import { Badge } from "@/components/ui/v2/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/v2/tooltip";

type IntegrationModuleChipsProps = {
  modules: IntegrationModuleChip[];
  maxVisible?: number;
};

function IntegrationModuleChips({
  modules,
  maxVisible = 3,
}: IntegrationModuleChipsProps) {
  const { t, i18n } = useTranslation();

  if (modules.length === 0) return null;

  const label = (module: IntegrationModuleChip) =>
    t(`integrations.modules.names.${module.moduleName}`, module.moduleName);

  const { visible, hidden } = orderAndSplitChips(modules, maxVisible, label, i18n.language);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {visible.map((module) => (
        <Badge key={module.moduleName} variant={module.isActive ? "success" : "neutral"}>
          {label(module)}
        </Badge>
      ))}

      {hidden.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" tabIndex={0}>
              {t("integrations.card.moreModules", { count: hidden.length })}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <ul className="space-y-1">
              {hidden.map((module) => (
                <li key={module.moduleName}>{label(module)}</li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export default IntegrationModuleChips;
